import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { landingsApi, launchesApi } from "../../api/endpoints";
import { mdPreview, mdToHtml } from "../../lib/md";
import { launchAvatarText } from "../../lib/launchAvatar";
import type { Landing, LandingStatus, Launch } from "../../types/domain";
import { LANDING_STATUS_META } from "./landingStatus";
import {
  IcoClose,
  IcoCopy,
  IcoDoc,
  IcoEdit,
  IcoPlus,
  IcoSearch,
  IcoTrash,
} from "../../components/icons";
import { CanWrite } from "../../components/CanWrite";
import { useCanWrite } from "../../hooks/useCanWrite";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { RenameDialog } from "../../components/RenameDialog";

type ViewMode = "grid" | "list";
type FilterLaunch = string | "all";
type FilterStatus = LandingStatus | "all";

const STATUS_META = LANDING_STATUS_META;

const pluralLandings = (n: number): string => {
  if (n === 1) return "лендинг";
  if (n > 1 && n < 5) return "лендинга";
  return "лендингов";
};

const pluralPages = (n: number): string => {
  if (n === 1) return "страница";
  if (n > 1 && n < 5) return "страницы";
  return "страниц";
};

export function LandingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [view, setView] = useState<ViewMode>("grid");
  const [query, setQuery] = useState("");
  const [filterLaunch, setFilterLaunch] = useState<FilterLaunch>("all");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [toDelete, setToDelete] = useState<Landing | null>(null);
  const [toRename, setToRename] = useState<Landing | null>(null);

  const landingsQuery = useQuery({
    queryKey: ["landings"],
    queryFn: async () => (await landingsApi.list()).landings,
  });
  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      landingsApi.create({ title: "Без названия", status: "draft", content: "" }),
    onSuccess: ({ landing }) => {
      queryClient.invalidateQueries({ queryKey: ["landings"] });
      navigate(`/landings/${landing.id}`);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (l: Landing) => {
      const { landing } = await landingsApi.create({
        title: `${l.title} (копия)`,
        status: "draft",
        content: l.content,
        launchId: l.launchId ?? null,
        url: l.url ?? null,
      });
      return landing;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["landings"] }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => landingsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landings"] });
      setToDelete(null);
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      landingsApi.patch(id, { title }),
    onSuccess: ({ landing }) => {
      queryClient.setQueryData(["landing", landing.id], landing);
      queryClient.invalidateQueries({ queryKey: ["landings"] });
      setToRename(null);
    },
  });

  const landings = landingsQuery.data ?? [];
  const launches = launchesQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return landings
      .filter((l) => {
        if (filterLaunch !== "all" && l.launchId !== filterLaunch) return false;
        if (filterStatus !== "all" && l.status !== filterStatus) return false;
        if (q) {
          const hay = (l.title + " " + (l.content || "")).toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
  }, [landings, query, filterLaunch, filterStatus]);

  const handleDelete = (l: Landing) => {
    setToDelete(l);
  };

  const hasActiveFilter = filterLaunch !== "all" || filterStatus !== "all" || query;

  return (
    <div className="launches-page landings-page">
      <div className="launches-head">
        <div>
          <h1 className="launches-title">Лендинги</h1>
          <div className="launches-subtitle">
            <b>{landings.length}</b> {pluralPages(landings.length)} по {launches.length}{" "}
            запускам
          </div>
        </div>
        <div className="launches-head-right">
          <div className="search" style={{ minWidth: 240 }}>
            <IcoSearch size={14} stroke="#7A849B" />
            <input
              placeholder="Поиск по тексту..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="view-toggle">
            <button
              type="button"
              className={"view-toggle-btn" + (view === "grid" ? " active" : "")}
              onClick={() => setView("grid")}
              title="Сетка"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
            <button
              type="button"
              className={"view-toggle-btn" + (view === "list" ? " active" : "")}
              onClick={() => setView("list")}
              title="Список"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <circle cx="4" cy="6" r="1" />
                <circle cx="4" cy="12" r="1" />
                <circle cx="4" cy="18" r="1" />
              </svg>
            </button>
          </div>
          <CanWrite>
            <button
              type="button"
              className="btn-primary"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              <IcoPlus size={14} stroke="white" sw={2.5} /> Новый лендинг
            </button>
          </CanWrite>
        </div>
      </div>

      <div className="landings-filters">
        <span className="filter-label">Запуск:</span>
        <select
          className="form-input mini"
          value={filterLaunch}
          onChange={(e) => setFilterLaunch(e.target.value as FilterLaunch)}
        >
          <option value="all">Все запуски</option>
          {launches.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <span className="filter-label" style={{ marginLeft: 8 }}>
          Статус:
        </span>
        <select
          className="form-input mini"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
        >
          <option value="all">Все</option>
          {(Object.keys(STATUS_META) as LandingStatus[]).map((k) => (
            <option key={k} value={k}>
              {STATUS_META[k].label}
            </option>
          ))}
        </select>
        {hasActiveFilter && (
          <button
            type="button"
            className="filter-chip"
            onClick={() => {
              setFilterLaunch("all");
              setFilterStatus("all");
              setQuery("");
            }}
            style={{ borderStyle: "dashed" }}
          >
            <IcoClose size={11} /> Сбросить
          </button>
        )}
        <div style={{ flex: 1 }} />
        <span className="filter-label">
          {filtered.length} {pluralLandings(filtered.length)}
        </span>
      </div>

      {landingsQuery.isLoading ? (
        <div className="launches-empty">
          <p>Загружаем лендинги…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="launches-empty">
          <div className="launches-empty-icon">
            <IcoDoc size={24} stroke="var(--text-muted)" />
          </div>
          <h3>{hasActiveFilter ? "Ничего не найдено" : "Пока нет лендингов"}</h3>
          <p>
            {hasActiveFilter
              ? "Попробуйте другой запрос или создайте новый."
              : "Создайте первый лендинг — каталог начнётся с него."}
          </p>
          <CanWrite>
            <button
              type="button"
              className="btn-primary"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              style={{ marginTop: 14 }}
            >
              <IcoPlus size={14} stroke="white" sw={2.5} /> Создать лендинг
            </button>
          </CanWrite>
        </div>
      ) : view === "grid" ? (
        <div className="landings-grid">
          {filtered.map((l) => (
            <LandingCard
              key={l.id}
              landing={l}
              launch={launches.find((x) => x.id === l.launchId) ?? null}
              onOpen={() => navigate(`/landings/${l.id}`)}
              onRename={() => setToRename(l)}
              onDuplicate={() => duplicateMutation.mutate(l)}
              onDelete={() => handleDelete(l)}
            />
          ))}
        </div>
      ) : (
        <LandingsList
          landings={filtered}
          launches={launches}
          onOpen={(id) => navigate(`/landings/${id}`)}
          onRename={(l) => setToRename(l)}
          onDuplicate={(l) => duplicateMutation.mutate(l)}
          onDelete={handleDelete}
        />
      )}
      {toRename && (
        <RenameDialog
          title="Переименовать лендинг"
          label="Название лендинга"
          placeholder="Название лендинга…"
          initialValue={toRename.title || ""}
          busy={renameMutation.isPending}
          onSave={(title) => renameMutation.mutate({ id: toRename.id, title })}
          onClose={() => setToRename(null)}
        />
      )}
      {toDelete && (
        <ConfirmDialog
          title="Удалить лендинг?"
          message={`«${toDelete.title || "Без названия"}» будет удалён без возможности восстановления.`}
          confirmLabel="Удалить лендинг"
          busy={removeMutation.isPending}
          onConfirm={() => removeMutation.mutate(toDelete.id)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}

interface CardProps {
  landing: Landing;
  launch: Launch | null;
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function LandingCard({ landing, launch, onOpen, onRename, onDuplicate, onDelete }: CardProps) {
  const canWrite = useCanWrite();
  const meta = STATUS_META[landing.status] ?? STATUS_META.draft;
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const miniHtml = useMemo(() => {
    const snippet = (landing.content || "").split("\n").slice(0, 6).join("\n");
    return mdToHtml(snippet);
  }, [landing.content]);

  return (
    <div className={`landing-card${menuOpen ? " menu-open" : ""}`} onClick={onOpen}>
      <div className="landing-card-preview">
        <div
          className="landing-card-mini-render"
          dangerouslySetInnerHTML={{ __html: miniHtml }}
        />
        <div className="landing-card-fade" />
      </div>
      <div className="landing-card-body">
        <div className="landing-card-title-row">
          <h3 className="landing-card-title">{landing.title || "Без названия"}</h3>
          <div
            className="launch-card-menu-wrap"
            ref={ref}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="launch-card-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              title="Действия"
              aria-label="Действия"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="5" cy="12" r="1.3" />
                <circle cx="12" cy="12" r="1.3" />
                <circle cx="19" cy="12" r="1.3" />
              </svg>
            </button>
            {menuOpen && (
              <div className="col-dropdown launch-card-dropdown">
                <button
                  type="button"
                  className="col-dropdown-item"
                  onClick={() => {
                    onOpen();
                    setMenuOpen(false);
                  }}
                >
                  <IcoDoc size={13} /> Открыть
                </button>
                {canWrite && (
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={() => {
                      onRename();
                      setMenuOpen(false);
                    }}
                  >
                    <IcoEdit size={13} /> Переименовать
                  </button>
                )}
                {canWrite && (
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={() => {
                      onDuplicate();
                      setMenuOpen(false);
                    }}
                  >
                    <IcoCopy size={13} /> Дублировать
                  </button>
                )}
                {landing.url && (
                  <a
                    className="col-dropdown-item"
                    href={landing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    Открыть страницу
                  </a>
                )}
                {canWrite && (
                  <>
                    <div className="col-dropdown-sep" />
                    <button
                      type="button"
                      className="col-dropdown-item col-dropdown-danger"
                      onClick={() => {
                        onDelete();
                        setMenuOpen(false);
                      }}
                    >
                      <IcoTrash size={13} /> Удалить
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="landing-card-foot">
          {launch && (
            <span className="landing-card-launch" title={launch.name}>
              <span
                className="launch-dot-xs"
                style={{ background: launch.color ?? undefined }}
              >
                {launchAvatarText(launch)}
              </span>
              <span className="landing-card-launch-name">{launch.name}</span>
            </span>
          )}
          <span
            className="landing-card-status"
            style={{ color: meta.color, background: meta.bg }}
          >
            <span className="status-dot" style={{ background: meta.dotColor }} />
            {meta.label}
          </span>
        </div>
      </div>
    </div>
  );
}

interface ListProps {
  landings: Landing[];
  launches: Launch[];
  onOpen: (id: string) => void;
  onRename: (l: Landing) => void;
  onDuplicate: (l: Landing) => void;
  onDelete: (l: Landing) => void;
}

function LandingsList({ landings, launches, onOpen, onRename, onDuplicate, onDelete }: ListProps) {
  const canWrite = useCanWrite();
  return (
    <div className="landings-list">
      <div className="landings-list-head">
        <div>Название</div>
        <div>Запуск</div>
        <div>Статус</div>
        <div>Обновлён</div>
        <div />
      </div>
      {landings.map((l) => {
        const launch = launches.find((x) => x.id === l.launchId);
        const meta = STATUS_META[l.status] ?? STATUS_META.draft;
        return (
          <div key={l.id} className="landings-list-row" onClick={() => onOpen(l.id)}>
            <div>
              <div className="landings-list-title">{l.title || "Без названия"}</div>
              <div className="landings-list-preview">{mdPreview(l.content, 90)}</div>
            </div>
            <div>
              {launch && (
                <span className="landing-card-launch">
                  <span
                    className="launch-dot-xs"
                    style={{ background: launch.color ?? undefined }}
                  >
                    {launchAvatarText(launch)}
                  </span>
                  <span className="landing-card-launch-name">{launch.name}</span>
                </span>
              )}
            </div>
            <div>
              <span
                className="landing-card-status"
                style={{ color: meta.color, background: meta.bg }}
              >
                <span className="status-dot" style={{ background: meta.dotColor }} />
                {meta.label}
              </span>
            </div>
            <div className="landings-list-meta">
              {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString("ru") : "—"}
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              {canWrite && (
                <>
                  <button
                    type="button"
                    className="launch-card-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRename(l);
                    }}
                    title="Переименовать"
                  >
                    <IcoEdit size={13} />
                  </button>
                  <button
                    type="button"
                    className="launch-card-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(l);
                    }}
                    title="Дублировать"
                  >
                    <IcoCopy size={13} />
                  </button>
                  <button
                    type="button"
                    className="launch-card-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(l);
                    }}
                    title="Удалить"
                  >
                    <IcoTrash size={13} />
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
